export type NormalizedGraphQLError = {
    message: string;
    path?: string;
    errorType?: string;
    code?: string | number;
    source?: string;
};

/** Distinct non-empty values, in first-seen order. */
export const distinct = (values: (string | undefined)[]) => Array.from(new Set(values.filter(Boolean)));

// TTV2-1919: every POST goes to one URL with no operation name, so the message itself names the
// operation and carries the ids an agent or a tester needs to find the failure:
// `<operationName>: <messages> [errorType=…; requestId=…]`, with "-" for a missing value.
const describeRequestError = (
    operationName: string | undefined,
    messages: string,
    normalized: NormalizedGraphQLError[],
    requestId: string | undefined
) =>
    `${operationName || "unknown"}: ${messages} ` +
    `[errorType=${distinct(normalized.map((e) => e.errorType)).join(",") || "-"}; requestId=${requestId || "-"}]`;

export class GraphQLRequestError<
    TVars extends Record<string, unknown> | undefined = Record<string, unknown>,
> extends Error {
    public name = "GraphQLRequestError";
    public operationType?: string;
    public operationName?: string;
    public errorsNormalized: NormalizedGraphQLError[];
    public variablesKeys: readonly string[];
    public fingerprint?: readonly string[];
    public requestId?: string;
    /** The messages alone (the server's, joined by "; "), without the operation name and ids of `message`. */
    public messages: string;

    constructor(params: {
        message: string;
        operationType?: string;
        operationName?: string;
        normalized: NormalizedGraphQLError[];
        variables?: TVars;
        requestId?: string;
    }) {
        super(describeRequestError(params.operationName, params.message, params.normalized, params.requestId));
        this.messages = params.message;
        this.operationType = params.operationType;
        this.operationName = params.operationName;
        this.errorsNormalized = params.normalized;
        this.variablesKeys = Object.keys(params.variables || {}).sort();
        this.requestId = params.requestId;
        const errorTypes = Array.from(
            new Set(this.errorsNormalized.map((e) => e.errorType || e.code || e.path || "unknown"))
        );
        this.fingerprint = ["graphql", this.operationName || "unknown", ...errorTypes.map(String)];
    }
}

export const extractGraphqlOperationName = (queryString: string): { type: string; name: string } | undefined => {
    const match = /\b(query|mutation|subscription)\s+(\w+)/.exec(queryString);
    return match ? { type: match[1], name: match[2] } : undefined;
};
